import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faChevronDown,
  faSearch,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";

const DropdownWithSearch = ({
  label,
  options,
  selectedValues = [],
  placeholder = "Select...",
  onSelect,
  allowMultiple = true,
  isLoading = false,
  isObjectArray = false,
  containerClassName = "relative w-full md:w-64",
  buttonClassName = "w-full flex justify-between items-center border px-4 py-2 rounded-md shadow-sm bg-white",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef(null);

  const filteredOptions = (options || []).filter((option) => {
    const displayValue = isObjectArray ? option?.label || "" : option || "";
    return displayValue
      .toString()
      .toLowerCase()
      .includes(searchQuery.toLowerCase());
  });

  const toTitleCase = (str = "") =>
    String(str)
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());

  const handleOptionClick = (option) => {
    const isOptionSelected = selectedValues.some((val) =>
      isObjectArray ? val?.value === option?.value : val === option,
    );
    const newSelected = allowMultiple
      ? isOptionSelected
        ? selectedValues.filter((val) =>
            isObjectArray ? val?.value !== option?.value : val !== option,
          )
        : [...selectedValues, option]
      : [option];
    onSelect(newSelected);
    setIsOpen(allowMultiple);
    if (!allowMultiple) {
      setSearchQuery("");
    }
  };

  const formatSelectedLabel = () => {
    if (!selectedValues || selectedValues.length === 0) return placeholder;
    const [first, second, ...rest] = selectedValues;
    let text = isObjectArray ? first?.label || "" : first || "";
    if (second)
      text += `, ${isObjectArray ? second?.label || "" : second || ""}`;
    if (rest.length) text += ` & +${rest.length}`;
    return text;
  };

  const isSelected = (option) => {
    return selectedValues.some((val) =>
      isObjectArray ? val?.value === option?.value : val === option,
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={dropdownRef} className={containerClassName}>
      <label className="block mb-1 text-sm font-medium text-gray-700">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClassName}
        disabled={isLoading}
      >
        <span className="truncate text-left">{formatSelectedLabel()}</span>
        {isLoading ? (
          <FontAwesomeIcon icon={faSpinner} className="ml-2 animate-spin" />
        ) : (
          <FontAwesomeIcon icon={faChevronDown} className="ml-2" />
        )}
      </button>

      {isOpen && (
        <div className="absolute mt-2 w-full bg-white border rounded-md shadow-lg z-30">
          <div className="p-2 border-b">
            <div className="flex items-center border rounded px-2">
              <FontAwesomeIcon icon={faSearch} className="text-gray-400 mr-2" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full p-1 text-sm outline-none"
              />
            </div>
          </div>
          <ul className="max-h-60 overflow-y-auto">
            {isLoading ? (
              <li className="px-4 py-2 text-center">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
              </li>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <li
                  key={isObjectArray ? option?.value : option}
                  onClick={() => handleOptionClick(option)}
                  className="flex justify-between items-center gap-2 px-4 py-2 cursor-pointer hover:bg-gray-100"
                >
                  <span
                    className="truncate"
                    title={toTitleCase(
                      isObjectArray ? (option?.label ?? "") : (option ?? ""),
                    )}
                  >
                    {toTitleCase(
                      isObjectArray ? (option?.label ?? "") : (option ?? ""),
                    )}
                  </span>
                  {isSelected(option) && (
                    <FontAwesomeIcon
                      icon={faCheck}
                      className="text-blue-600 w-4 h-4"
                    />
                  )}
                </li>
              ))
            ) : (
              <li className="px-4 py-2 text-gray-500">No options found</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DropdownWithSearch;
